import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Send } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICES, BUDGET_OPTIONS, TIMELINE_OPTIONS, IMG } from "@/data/content";

const inputCls = "border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus-visible:border-[#FF4500] focus-visible:ring-[#FF4500]/30";
const STEPS = ["Diensten", "Budget & planning", "Jouw gegevens"];

export default function Offerte() {
  useSeo({
    title: "Offerte aanvragen | Imperium Group",
    description: "Stel in een paar stappen jouw marketingpakket samen en vraag vrijblijvend een offerte aan bij Imperium Group — van één kanaal tot een complete 360°-campagne.",
    path: "/offerte",
  });

  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    services: [], budget: "", timeline: "",
    name: "", company: "", email: "", phone: "", message: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleService = (title) => setForm((f) => ({
    ...f,
    services: f.services.includes(title) ? f.services.filter((s) => s !== title) : [...f.services, title],
  }));

  const next = () => {
    if (step === 0 && form.services.length === 0) { toast.error("Kies minimaal één dienst."); return; }
    setStep((s) => Math.min(s + 1, 2));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Vul minimaal je naam en e-mailadres in."); return; }
    setLoading(true);
    try {
      await api.post("/quote", form);
      navigate("/bedankt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Er ging iets mis. Probeer het opnieuw.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="offerte-page">
      <PageHero
        overline="Offerte aanvragen"
        title="Stel jouw marketingpakket samen"
        subtitle="In drie korte stappen vertel je ons wat je nodig hebt. Je zit nergens aan vast — wij nemen contact op met een voorstel op maat."
        image={IMG.analytics}
      />

      <section className="pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          {/* Progress */}
          <div className="mb-10 flex items-center justify-between" data-testid="offerte-progress">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-1 flex-col items-center gap-2">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${i <= step ? "border-[#FF4500] bg-[#FF4500] text-[#090B0E]" : "border-white/15 bg-transparent text-slate-500"}`}>
                    {i < step ? <Check className="h-5 w-5" /> : i + 1}
                  </div>
                  <span className={`text-center text-xs ${i <= step ? "text-white" : "text-slate-500"}`}>{label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`mx-1 h-px flex-1 ${i < step ? "bg-[#FF4500]" : "bg-white/10"}`} />}
              </React.Fragment>
            ))}
          </div>

          <Reveal key={step}>
            <div className="rounded-3xl border border-white/10 bg-[#12151D] p-8 lg:p-10">
              {step === 0 && (
                <div data-testid="offerte-step-services">
                  <h2 className="font-display text-xl font-semibold text-white lg:text-2xl">Welke diensten heb je nodig?</h2>
                  <p className="mt-2 text-sm text-slate-400">Selecteer alles wat van toepassing is — combineer gerust meerdere kanalen.</p>
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {SERVICES.map((s) => {
                      const active = form.services.includes(s.title);
                      const Icon = s.icon;
                      return (
                        <button type="button" key={s.slug} onClick={() => toggleService(s.title)} data-testid={`offerte-service-${s.slug}`}
                          className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${active ? "border-[#FF4500] bg-[#FF4500]/10" : "border-white/10 bg-[#090B0E] hover:border-white/25"}`}>
                          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${active ? "bg-[#FF4500] text-[#090B0E]" : "bg-[#FF4500]/10 text-[#FF4500]"}`}><Icon className="h-5 w-5" /></span>
                          <span className="text-sm font-medium text-white">{s.title}</span>
                          {active && <Check className="ml-auto h-4 w-4 text-[#FF4500]" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="offerte-step-budget" className="space-y-8">
                  <div>
                    <h2 className="font-display text-xl font-semibold text-white lg:text-2xl">Wat is je indicatieve budget?</h2>
                    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {BUDGET_OPTIONS.map((b) => (
                        <button type="button" key={b} onClick={() => setForm((f) => ({ ...f, budget: b }))} data-testid={`offerte-budget-${b.slice(0,4)}`}
                          className={`rounded-xl border p-4 text-left text-sm font-medium transition-colors ${form.budget === b ? "border-[#FF4500] bg-[#FF4500]/10 text-white" : "border-white/10 bg-[#090B0E] text-slate-300 hover:border-white/25"}`}>
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h2 className="font-display text-xl font-semibold text-white lg:text-2xl">Wanneer wil je starten?</h2>
                    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {TIMELINE_OPTIONS.map((t) => (
                        <button type="button" key={t} onClick={() => setForm((f) => ({ ...f, timeline: t }))} data-testid={`offerte-timeline-${t.slice(0,4)}`}
                          className={`rounded-xl border p-4 text-left text-sm font-medium transition-colors ${form.timeline === t ? "border-[#FF4500] bg-[#FF4500]/10 text-white" : "border-white/10 bg-[#090B0E] text-slate-300 hover:border-white/25"}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="offerte-step-details">
                  <h2 className="font-display text-xl font-semibold text-white lg:text-2xl">Jouw gegevens</h2>
                  {form.services.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="offerte-summary">
                      {form.services.map((s) => (<span key={s} className="rounded-full border border-[#FF4500]/30 bg-[#FF4500]/5 px-3 py-1 text-xs font-medium text-[#FF4500]">{s}</span>))}
                    </div>
                  )}
                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Naam *"><Input data-testid="offerte-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jouw naam" /></Field>
                    <Field label="Bedrijfsnaam"><Input data-testid="offerte-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Bedrijfsnaam" /></Field>
                    <Field label="E-mail *"><Input data-testid="offerte-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="naam@bedrijf.nl" /></Field>
                    <Field label="Telefoon"><Input data-testid="offerte-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Telefoon" /></Field>
                  </div>
                  <div className="mt-5"><Field label="Toelichting"><Textarea data-testid="offerte-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Vertel ons kort over je doelen of project." /></Field></div>
                </div>
              )}

              {/* Nav */}
              <div className="mt-8 flex items-center justify-between gap-4">
                {step > 0 ? (
                  <button type="button" onClick={back} data-testid="offerte-back" className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/30">
                    <ArrowLeft className="h-4 w-4" /> Terug
                  </button>
                ) : <span />}
                {step < 2 ? (
                  <button type="button" onClick={next} data-testid="offerte-next" className="inline-flex items-center gap-2 rounded-xl bg-[#FF4500] px-7 py-3 text-sm font-semibold text-[#090B0E] transition-[background-color,transform] duration-200 hover:bg-[#ff6428] active:scale-95">
                    Volgende <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={loading} data-testid="offerte-submit" className="inline-flex items-center gap-2 rounded-xl bg-[#FF4500] px-7 py-3 text-sm font-semibold text-[#090B0E] transition-[background-color,transform] duration-200 hover:bg-[#ff6428] active:scale-95 disabled:opacity-60">
                    {loading ? "Bezig..." : "Offerte aanvragen"} <Send className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-2"><Label className="text-sm text-slate-300">{label}</Label>{children}</div>);
}
